import { useEffect, useMemo, useState } from 'react';
import colors from '../../../constants/colors.constants';
import Typo from '../../../components/atom/Typo';
import { useQuery } from 'react-query';
import useAuthStore from '../../../store/useAuthStore';
import ProductItem from '../TreatmentReservationPage/components/ProductItem';
import Button from '../../../components/atom/Button';
import strings from '../../../constants/strings.constants';
import useProductOptionsStore from '../../../store/useProductOptionsStore';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import { ReactComponent as ExpandToggleIcon } from '../../../assets/icon-arrow-menu-toggle.svg';
import { ReactComponent as InfoIcon } from '../../../assets/svgs/icon-info.svg';
import {
  formatCurrency,
  getProductOptionTotalPrice,
} from '../../../utils/utils';
import { Fade } from 'react-awesome-reveal';
import Skeleton from 'react-loading-skeleton';
import { fetchExhibitionDetail } from '../../../apis/exhibitions';
import moment from 'moment';
import SelectedProductOptionItem from '../TreatmentReservationPage/components/SelectedProductOptionItem';
import MobileModal from '../../../components/compositions/MobileModal/MobileModal';
import {
  sendTutorialTreatmentSelectedEvent,
  useTutorialStore,
} from '../../../store/useTutorialStore';
import { useSearchParams } from 'react-router-dom';
import URL_QUERY_KEYS from '../../../constants/urlQueryKeys.constants';
import {
  sendRemoveFromCartEvent,
  sendSelectPromotionEvent,
  sendViewPromotionEvent,
} from '../../../utils/ga-utils';

export interface ExhibitionDetailModalProps {
  exhibitionId?: number;
  visible?: boolean;
  onCancel?: () => void;
  onSelected?: () => void;
}

function ExhibitionDetailModal(props: ExhibitionDetailModalProps) {
  const { exhibitionId, visible = false, onCancel, onSelected } = props;
  const { partner } = useAuthStore();
  const { productOptions, remove } = useProductOptionsStore();
  const { nextStep } = useTutorialStore();
  const [searchParams] = useSearchParams();
  const isTut = !!searchParams.get('tut');
  const [footerExpanded, setFooterExpanded] = useState(true);
  const isTempData = searchParams.get(URL_QUERY_KEYS.IS_TEMP_DATA) === 'true';

  const isIncludeVAT = useMemo(() => {
    return (
      partner?.globalHomepageConfig?.isIncludeVat ||
      partner?.homepageConfig?.isIncludeVat
    );
  }, [partner]);

  const { data: exhibitionDetail, isLoading: isLoadingExhibition } = useQuery({
    queryKey: `FETCH_EXHIBITION_DETAIL_${partner?.id}_${exhibitionId}`,
    queryFn: async () => {
      return fetchExhibitionDetail(
        partner?.partnerCode || '',
        exhibitionId || '',
        isTempData,
      );
    },
    retry: false,
    enabled: !!partner && !!exhibitionId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isTempData) {
      return;
    }
    if (!visible) {
      return;
    }
    if (!exhibitionDetail) {
      return;
    }

    sendSelectPromotionEvent({
      promotionId: exhibitionDetail.id,
      promotionName: exhibitionDetail.title,
    });
  }, [exhibitionDetail, isTempData, visible]);

  const renderSelectedProductOptions = () => {
    return (
      <div
        className="flex flex-col gap-4"
        style={{
          borderTop: `1px solid ${colors.defaultBorder}`,
          padding: '12px 0',
          maxHeight: '40vh',
          overflowY: 'auto',
        }}
      >
        {productOptions.map((productOption) => (
          <SelectedProductOptionItem
            key={productOption.id}
            productOption={productOption}
            onDeleteClick={({ id }) => {
              remove(id);
              sendRemoveFromCartEvent([productOption]);
            }}
          />
        ))}
      </div>
    );
  };

  const renderExhibitionDetail = () => {
    if (!exhibitionDetail) return null;

    const {
      title,
      description,
      thumbnailUrl,
      startedAt,
      endedAt,
      events = [],
      categories = [],
    } = exhibitionDetail;

    return (
      <div>
        <div className="flex flex-col items-center mb-3 gap-1">
          <Typo
            style={{
              fontSize: 20,
            }}
            type="b-18"
          >
            {title}
          </Typo>
          {description && (
            <div>
              <Typo type="r-14" color={colors.slate500}>
                {description}
              </Typo>
            </div>
          )}
        </div>
        <div
          style={{
            overflow: 'hidden',
          }}
        >
          {thumbnailUrl && <img src={thumbnailUrl} alt={title} />}
        </div>

        <div className="mt-[24px] flex flex-col gap-4">
          {events.map((event, eventIndex) => {
            const { eventStartedAt, eventEndedAt } = event;
            let eventTermText = '상시 이벤트';
            let startDateText = '';
            let endDateText = '';
            if (eventStartedAt) {
              startDateText = `${moment(startedAt).format('YY.MM.DD')}`;
            }
            if (eventEndedAt) {
              endDateText = `${moment(endedAt).format('YY.MM.DD')} 까지`;
            }

            if (startDateText && endDateText) {
              eventTermText = `${startDateText} ~ ${endDateText}`;
            } else if (endDateText) {
              eventTermText = `${endDateText} 까지`;
            }
            return (
              <div className="flex flex-col gap-4" key={event.id}>
                <div className="flex flex-col gap-1">
                  <Typo type="b-18">{event.eventTitle}</Typo>
                  <Typo type="r-14" color={colors.slate500}>
                    {eventTermText}
                  </Typo>
                </div>

                <div className="flex flex-col gap-3">
                  {event.products.map((product, index) => {
                    return (
                      <ProductItem
                        tutorialProduct={index === 0 && eventIndex === 0}
                        key={product.id}
                        product={product}
                        exhibitionId={exhibitionId}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          {categories.map((category) => {
            const { productCategoryName } = category;

            return (
              <div className="flex flex-col gap-4" key={category.id}>
                <div className="flex flex-col gap-1">
                  <Typo type="b-18">{productCategoryName}</Typo>
                </div>

                <div className="flex flex-col gap-3">
                  {category.products.map((product) => {
                    return (
                      <ProductItem
                        key={product.id}
                        product={product}
                        exhibitionId={exhibitionId}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <MobileModal
      visible={visible}
      onCancel={onCancel}
      backButtonAction={() => {
        if (onCancel) onCancel();
      }}
      footer={
        <Footer className="shrink-0">
          <div>
            <div
              className="flex justify-between"
              style={{
                cursor: 'pointer',
                paddingBottom: 12,
              }}
              onClick={() => {
                setFooterExpanded(!footerExpanded);
              }}
            >
              <div className="flex gap-1 items-center">
                <Typo type="m-14">{strings.expectedPrice}</Typo>
                <Typo type="m-12" color={colors.subTitle2}>
                  {isIncludeVAT ? strings.includeVAT : strings.excludeVAT}
                </Typo>
              </div>

              <div className="flex items-center gap-2">
                <Typo type="b-18">
                  {formatCurrency(getProductOptionTotalPrice(productOptions))}
                  {strings.priceUnit}
                </Typo>
                <ToggleIconWrapper $open={!footerExpanded}>
                  <ExpandToggleIcon
                    style={{
                      width: 14,
                      height: 14,
                      color: '#8C939A',
                    }}
                  />
                </ToggleIconWrapper>
              </div>
            </div>
            <Collapse isOpened={footerExpanded}>
              {productOptions.length > 0 && renderSelectedProductOptions()}
            </Collapse>
          </div>
          <Button
            id="tutorial-next1"
            style={{
              width: '100%',
              height: '72px',
            }}
            disabled={productOptions.length === 0}
            onClick={() => {
              if (isTut) {
                setTimeout(() => {
                  nextStep();
                }, 500);
                sendTutorialTreatmentSelectedEvent();
              }
              if (onSelected) onSelected();
            }}
          >
            <div className="flex flex-col gap-[4px]">
              <div className="justify-center flex items-center gap-2">
                <Typo
                  style={{
                    color:
                      productOptions.length > 0
                        ? 'white'
                        : colors.btnDisabledText,
                  }}
                  type="m-16"
                >
                  {strings.makeReservation}
                </Typo>
                {productOptions.length > 0 && (
                  <span
                    className="rounded"
                    style={{
                      color: colors.primary3,
                      background: 'white',
                      padding: 3,
                      minWidth: 24,
                      borderRadius: 12,
                      fontSize: 15,
                      fontWeight: 500,
                    }}
                  >
                    {productOptions.length}
                  </span>
                )}
              </div>

              <Typo
                type="r-14"
                style={{
                  fontSize: 13,
                  color:
                    productOptions.length > 0
                      ? '#BFDBFE'
                      : colors.btnDisabledText,
                }}
              >
                <div className="flex gap-1 justify-center">
                  <InfoIcon
                    color={
                      productOptions.length > 0
                        ? '#BFDBFE'
                        : colors.btnDisabledText
                    }
                  />
                  {strings.productPriceNoticeDesc}
                </div>
              </Typo>
            </div>
          </Button>
        </Footer>
      }
    >
      <div className="h-full flex flex-col">
        {/*  Content */}
        <div className="h-full flex-1 overflow-y-auto py-[16px]">
          <Fade duration={200}>
            <div className="flex flex-col gap-3 bg-white px-[16px]">
              {isLoadingExhibition ? (
                <Skeleton
                  style={{
                    height: 100,
                    marginBottom: 12,
                    lineHeight: 1.5,
                    borderRadius: 16,
                  }}
                  count={5}
                />
              ) : (
                renderExhibitionDetail()
              )}
            </div>
          </Fade>
        </div>
        {/*  Footer */}
      </div>
    </MobileModal>
  );
}

export default ExhibitionDetailModal;

const Footer = styled.div`
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px 20px 32px 20px;
  position: relative;
  box-shadow: 0px -4px 30px 0px #0000001f;
`;

const ToggleIconWrapper = styled.div<{ $open: boolean }>`
  & > svg {
    transition: all 0.2s;
    transform: ${(props) => {
      if (props.$open) return 'rotate(180deg)';
      return 'rotate(0)';
    }};
  }
`;
